"use client"

import { type ReactNode, useState } from "react"

import { Button } from "@/components/ui/button"
import { EditableText } from "@/components/ui/editable-text"
import { FileUpload } from "@/components/ui/file-upload"
import { HintText, InputBase, Label, TextField } from "@/components/ui/input"
import { ProseField } from "@/components/ui/prose-editor"
import { SectionFooter } from "@/components/ui/section-footer"
import { TextEditor } from "@/components/ui/text-editor"
import { Toggle } from "@/components/ui/toggle"
import { cn } from "@/lib/utils"
import type { WarmStartClaim } from "@/types"

import {
  type MarginNoteEditor,
  MarginNotes,
  MarginRow,
  type NoteAnchor,
} from "./claim-margin"

export interface PlaylistFormData {
  title: string
  description: string
  cover_image: File | null
  published: boolean
}

interface PlaylistFormProps {
  data: PlaylistFormData
  setData: (next: PlaylistFormData) => void
  errors: Record<string, string | undefined>
  processing: boolean
  /** Initial HTML content for the description editor (uncontrolled). */
  initialDescription: string
  /** Existing cover image URL to seed the preview (edit mode). */
  initialCoverUrl?: string | null
  /** Error key for the cover image field — differs between new/edit. */
  coverErrorKey: string
  submitLabel: string
  removeLabel: string
  onSubmit: (e: React.FormEvent) => void
  onCancel: () => void
  /** Notifies the parent when the existing cover is removed (edit submit logic). */
  onCoverRemovedChange?: (removed: boolean) => void
  /**
   * Warm-start claim payload. Present only when the recipient opened this
   * editor via their invite link. In claim mode the editor becomes a two-column
   * manuscript: the playlist in the main column, the growth manager's self-
   * dimming margin notes in a continuous right margin (and the meta card is
   * dropped — publish is forced server-side).
   */
  claim?: WarmStartClaim | null
  /**
   * Extra content rendered between the fields and the footer. In claim mode the
   * editor drops the tactile track board here so the recipient sees and shapes
   * the actual playlist in one surface, sharing the same margin.
   */
  claimSlot?: ReactNode
  /** Disables submit beyond `processing` — e.g. the track board is empty. */
  submitDisabled?: boolean
  /**
   * Fired when a claim-mode word field (title/description) commits — blur, the
   * edit-surface commit point. The coordinator editor persists on it; the
   * recipient editor leaves it undefined (words save at publish).
   */
  onWordsCommit?: () => void
  /**
   * Whether the Cancel/submit footer renders. Off on the coordinator surface,
   * which has no publish action — its exits live in the banner.
   */
  showFooter?: boolean
  /**
   * Builds the margin-note persistence handlers for an anchor. Present only on
   * the coordinator surface, where the margin notes are authored, not just
   * read — the recipient editor leaves it undefined.
   */
  noteEditorFor?: (anchor: NoteAnchor) => MarginNoteEditor
}

/**
 * PlaylistForm component.
 * - Normal/new mode: two-column layout — content fields beside the meta card.
 * - Claim mode ("Make it yours"): a centred-width manuscript — the title and
 *   description run down the main column, each annotated by a margin note in a
 *   continuous right margin that the track board (claimSlot) shares.
 */
export default function PlaylistForm({
  data,
  setData,
  errors,
  processing,
  initialDescription,
  initialCoverUrl = null,
  coverErrorKey,
  submitLabel,
  removeLabel,
  onSubmit,
  onCancel,
  onCoverRemovedChange,
  claim = null,
  claimSlot,
  submitDisabled = false,
  onWordsCommit,
  showFooter = true,
  noteEditorFor,
}: PlaylistFormProps) {
  const [coverPreview, setCoverPreview] = useState<string | null>(
    initialCoverUrl,
  )

  // Field notes for the singleton fields rendered here (title/description).
  // Track-anchored notes are rendered by the board, in the same margin.
  const notesFor = (field: string) =>
    claim?.field_notes.filter((note) => note.anchor.field === field) ?? []

  const handleCoverSelect = (files: FileList) => {
    const file = files[0]
    if (file) {
      onCoverRemovedChange?.(false)
      setData({ ...data, cover_image: file })
      const reader = new FileReader()
      reader.onload = (e) => {
        setCoverPreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveCover = () => {
    onCoverRemovedChange?.(true)
    setData({ ...data, cover_image: null })
    setCoverPreview(null)
  }

  const titleField = (
    <div className="flex flex-col gap-2">
      <TextField
        isRequired
        name="playlist[title]"
        value={data.title}
        onChange={(v) => setData({ ...data, title: v })}
        isInvalid={!!errors["playlist.title"]}
      >
        <Label>Title</Label>
        <InputBase size="md" placeholder="Enter playlist title" />
        {errors["playlist.title"] && (
          <HintText isInvalid>{errors["playlist.title"]}</HintText>
        )}
      </TextField>
    </div>
  )

  const descriptionField = (
    <div className="flex flex-col gap-2">
      <TextEditor.Root
        content={initialDescription}
        onUpdate={({ editor }) =>
          setData({ ...data, description: editor.getHTML() })
        }
        placeholder="Add the description of your playlist. (optional)"
        isInvalid={!!errors["playlist.description"]}
        inputClassName="min-h-30 resize-y"
      >
        <TextEditor.Label>Description</TextEditor.Label>
        <TextEditor.Toolbar type="simple" />
        <TextEditor.Content />
        {errors["playlist.description"] && (
          <TextEditor.HintText>
            {errors["playlist.description"]}
          </TextEditor.HintText>
        )}
      </TextEditor.Root>
    </div>
  )

  // Claim-mode words: the title and description rendered as the artifact the
  // recipient is making theirs — borderless, in the published voice, edited in
  // place — not as a form to fill. The seeded value is the gift; the margin
  // note beside it is the instruction (see docs/design/edit-surfaces.md).
  const claimTitleField = (
    <div className="flex flex-col gap-1">
      <EditableText
        presentation="display"
        ariaLabel="Playlist title"
        value={data.title}
        onChange={(v) => setData({ ...data, title: v })}
        onCommit={onWordsCommit}
        placeholder="Name your playlist"
        isInvalid={!!errors["playlist.title"]}
      />
      {errors["playlist.title"] && (
        <p className="text-error-primary text-sm">{errors["playlist.title"]}</p>
      )}
    </div>
  )

  const claimDescriptionField = (
    <div className="flex flex-col gap-1">
      <ProseField
        ariaLabel="Playlist description"
        initialContent={initialDescription}
        onUpdate={(html) => setData({ ...data, description: html })}
        onBlur={onWordsCommit}
        placeholder="Say a little about why these tracks belong together…"
        isInvalid={!!errors["playlist.description"]}
      />
      {errors["playlist.description"] && (
        <p className="text-error-primary text-sm">
          {errors["playlist.description"]}
        </p>
      )}
    </div>
  )

  // Meta card (cover + visibility). Normal mode only — in claim mode publish is
  // forced server-side and the right column is the margin, not a meta panel.
  const metaCard = (
    <div className="lg:col-span-2">
      <div className="border-secondary bg-primary flex flex-col gap-5 rounded-2xl border p-5 lg:sticky lg:top-6">
        {/* Cover image */}
        <div className="flex flex-col gap-1.5">
          <Label>Cover Image</Label>
          {coverPreview ? (
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src={coverPreview}
                alt="Cover preview"
                className="aspect-video w-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition hover:opacity-100">
                <Button
                  type="button"
                  color="secondary"
                  size="sm"
                  onClick={handleRemoveCover}
                >
                  {removeLabel}
                </Button>
              </div>
            </div>
          ) : (
            <FileUpload.Root>
              <FileUpload.DropZone
                accept="image/*"
                allowsMultiple={false}
                hint="PNG, JPG, GIF or WebP (max. 5MB)"
                maxSize={5 * 1024 * 1024}
                onDropFiles={handleCoverSelect}
              />
            </FileUpload.Root>
          )}
          {errors[coverErrorKey] && (
            <HintText isInvalid>{errors[coverErrorKey]}</HintText>
          )}
        </div>

        {/* Published toggle */}
        <Toggle
          isSelected={data.published}
          onChange={(isSelected) => setData({ ...data, published: isSelected })}
          size="md"
          hint={
            data.published
              ? "This playlist is published and visible to everyone."
              : "This playlist is unpublished and only visible to you."
          }
          label={data.published ? "Published" : "Unpublished"}
          slim
        />
      </div>
    </div>
  )

  return (
    <form
      onSubmit={onSubmit}
      className={cn("flex flex-col gap-8", claim && "lg:max-w-5xl")}
    >
      {claim ? (
        <>
          {/* No welcome banner: the recipient arrives here straight from "Open
              my playlist" and can already see the filled draft — announcing it
              would over-tell the obvious. The coordinator's presence is carried,
              shown not told, by the avatar-led margin notes (edit-surfaces.md). */}
          {/* Title + description, each annotated in the shared right margin. */}
          <div className="flex flex-col gap-6">
            <MarginRow
              aside={
                <MarginNotes
                  notes={notesFor("title")}
                  authorName={claim.created_by_name}
                  avatarUrl={claim.created_by_avatar_url}
                  currentValue={data.title}
                  editor={noteEditorFor?.({ field: "title" })}
                />
              }
            >
              {claimTitleField}
            </MarginRow>
            <MarginRow
              aside={
                <MarginNotes
                  notes={notesFor("description")}
                  authorName={claim.created_by_name}
                  avatarUrl={claim.created_by_avatar_url}
                  currentValue={data.description}
                  editor={noteEditorFor?.({ field: "description" })}
                />
              }
            >
              {claimDescriptionField}
            </MarginRow>
          </div>

          {claimSlot}
        </>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5 lg:gap-8">
          <div className="flex flex-col gap-5 lg:col-span-3">
            {titleField}
            {descriptionField}
          </div>
          {metaCard}
        </div>
      )}

      {/* The coordinator surface has no publish action, so no footer. */}
      {showFooter && (
        <SectionFooter.Root>
          <SectionFooter.Actions>
            <Button
              type="button"
              color="secondary"
              size="md"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              size="md"
              isDisabled={processing || submitDisabled}
              isLoading={processing}
            >
              {submitLabel}
            </Button>
          </SectionFooter.Actions>
        </SectionFooter.Root>
      )}
    </form>
  )
}
